// src/pages/QueueListPage.js
//
// Visão alternativa da fila do dia, em lista vertical ordenável.
// A visão de mapa (QueuePlanner, em "/") continua funcionando igual.
import React, { useEffect, useState } from "react";
import { Button, Spin, message } from "antd";
import { AppstoreOutlined, PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

import DayBar from "../components/DayBar";
import ProductModal from "../components/ProductModal";
import QueueList from "../components/QueueList";
import {
  fetchQueue,
  createQueueItem,
  reorderQueue,
  updateQueueItem,
  deleteQueueItem,
} from "../api/queueApi";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function QueueListPage() {
  const [day, setDay] = useState(todayISO());
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchQueue(day);
      setItems(data.items || []);
    } catch (e) {
      message.error(e.message || "Erro ao buscar fila");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  const handleCreate = async (values) => {
    const { item } = await createQueueItem({
      day,
      status: "READY",
      ...values,
    });

    setItems((prev) => [...prev, item]);
    setOpen(false);
  };

  const handleUpdate = async (values) => {
    try {
      const { item } = await updateQueueItem(editing.id, {
        ...values,
        priceFrom: values.priceFrom ?? null,
        priceTo: values.priceTo ?? null,
      });

      setItems((prev) =>
        prev.map((p) => (p.id === editing.id ? item : p))
      );

      message.success("Atualizado");
      setOpen(false);
    } catch (e) {
      message.error(e.message || "Erro ao atualizar");
    }
  };

  const handleDelete = async (item) => {
    try {
      await deleteQueueItem(item.id);
      setItems((prev) => prev.filter((p) => p.id !== item.id));
      message.success("Deletado");
    } catch (e) {
      message.error(e.message || "Erro ao deletar");
    }
  };

  // A lista não usa posições x/y (isso é do mapa), só a ordem.
  const onSaveOrder = async (orderedIds) => {
    const { items: updated } = await reorderQueue(day, orderedIds);
    setItems(updated || []);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8f9fa",
        color: "#202124",
      }}
    >
      {/* topo fixo, branco */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "#ffffff",
          borderBottom: "1px solid #e3e6ea",
          padding: "14px 20px",
        }}
      >
        <div
          style={{
            maxWidth: 820,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 500,
                letterSpacing: "-.2px",
              }}
            >
              Fila do dia
            </h1>

            <DayBar day={day} onChange={setDay} light />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Link to="/">
              <Button type="text" icon={<AppstoreOutlined />}>
                Mapa
              </Button>
            </Link>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
              style={{ borderRadius: 999, boxShadow: "none" }}
            >
              Produto
            </Button>
          </div>
        </div>
      </header>

      <main style={{ padding: "24px 20px 60px" }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: "center" }}>
            <Spin />
          </div>
        ) : (
          <QueueList
            items={items}
            onEdit={(item) => {
              setEditing(item);
              setOpen(true);
            }}
            onDelete={handleDelete}
            onSaveOrder={onSaveOrder}
          />
        )}
      </main>

      <ProductModal
        open={open}
        editing={editing}
        onCancel={() => setOpen(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />
    </div>
  );
}
